import { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import { FormFieldset, SelectField, TextField } from '@Forms/fields.jsx';
import { urlFormat } from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

const parseFormattedURL = (formattedURL) => {
  // take a full URL and then parse into formatted_domain, base_directory and https
  // for use with our API.
  //
  // domain validation is handled by the backend/API
  const urlObj = new URL(formattedURL);

  return {
    formatted_domain: urlObj.hostname,
    base_directory: urlObj.pathname,
    https: urlObj.protocol == 'https:' ? true : false,
  };
};

export const WebsiteForm = ({
  businessData,
  createSite,
  createTheme,
  disabled,
  onCancel,
  siteData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    setFocus,
  } = useForm({
    defaultValues: siteData,
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  useEffect(() => {
    // set focus to the name field if this is a new project
    setFocus('type_to_add');
  }, [setFocus]);

  const onSubmit = async (data) => {
    // parse the formatted_url into its parts and update the payload
    const parsed = parseFormattedURL(data.formatted_url);
    data = {
      ...data,
      ...parsed,
    };
    // create the blank theme
    const theme = await createTheme.mutateAsync({
      defer_jquery: true,
      display_name: 'Primary site theme',
      jquery_asset: 'https://code.jquery.com/jquery-3.6.0.slim.min.js',
      render_mode: 'legacy',
    });
    // create site, assign the theme
    const site = await createSite.mutateAsync(
      {
        ...data,
        business_id: businessData.id,
        locations: [], // no locations assigned
        theme_id: theme.id,
        title: 'Primary site',
        type: 'default',
      },
      {
        onError: (error) => {
          // handle server-side validations and errors
          const errorsList = errorsFromMutate(error);
          // Only set field errors, skip __all__ errors as they are handled in the parent component
          errorsList
          .filter(({ field }) => field !== '__all__')
          .forEach(({ field, error }) => setError(field, error));
          },
      },
    );
  };

  // state used based on the currently selected clientside_type
  const [typeToAdd, setTypeToAdd] = useState(false);
  // change event on service area toggle
  const typeToAddChange = (e) => {
    setTypeToAdd(e.target.value);
  };

  // combined isPending and isError
  const isError = createSite.isError || createTheme.isError;
  const isPending = createSite.isPending || createTheme.isPending;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      {!typeToAdd && (
        <FormFieldset>
          <SelectField
            register={register}
            error={errors.type_to_add}
            options={{
              required: 'This field is required',
              onChange: typeToAddChange,
            }}
            name="type_to_add"
            label="Type of website"
            helpText="Primary site - Website typically hosted at the root of the Project's Primary domain (yourbrand.com)"
          >
            <option value="">Choose a type...</option>
            <option value="primary">Primary website</option>
          </SelectField>
        </FormFieldset>
      )}
      {typeToAdd == 'primary' && (
        <FormFieldset legend="Adding a primary website">
          <TextField
            register={register}
            error={errors.formatted_url}
            options={{
              required: 'This field is required',
              onChange: urlFormat,
            }}
            name="formatted_url"
            label="Published URL"
            placeholder="https://www.example.com/yoursite/"
            helpText="Primary URL for this Website and its sub-pages"
          />
          <TextField
            register={register}
            error={errors.title}
            options={{ required: 'This field is required' }}
            name={`title`}
            label="Website label"
            helpText="Friendly label for this website. Will not show on the published website"
          />
        </FormFieldset>
      )}
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          <button
            className="sb-btn-cancel sb-btn"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {isPending ? 'Creating...' : 'Create new'}
            {isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
      </div>
    </form>
  );
};
